import type { ValidationRule, RuleCondition } from '@mayksel/types';

export type ValidationTrigger = 'on_create' | 'on_update' | 'on_stage_change';

export interface ValidationViolation {
  ruleId: string;
  ruleName: string;
  message: string;
  actionType: 'require_field' | 'prevent_save' | 'show_warning';
  /** The field referenced by a require_field action */
  field?: string;
}

/**
 * Run active validation rules against entity data for a given trigger.
 *
 * Returns an array of violations. Callers should:
 * - Block saves for `prevent_save` and `require_field` violations.
 * - Show warnings (but allow saving) for `show_warning` violations.
 */
export function runValidationRules(
  rules: ValidationRule[],
  entityData: Record<string, unknown>,
  trigger: ValidationTrigger,
): ValidationViolation[] {
  const violations: ValidationViolation[] = [];

  for (const rule of rules) {
    if (!rule.isActive) continue;
    if (rule.trigger !== trigger) continue;

    if (evaluateCondition(rule.condition, entityData)) {
      const { type, field } = rule.action;

      if (type === 'require_field') {
        const targetField = field ?? '';
        if (isBlank(entityData[targetField])) {
          violations.push({
            ruleId: rule.id,
            ruleName: rule.name,
            message: rule.errorMessage,
            actionType: 'require_field',
            field: targetField,
          });
        }
      } else if (type === 'prevent_save') {
        violations.push({
          ruleId: rule.id,
          ruleName: rule.name,
          message: rule.errorMessage,
          actionType: 'prevent_save',
        });
      } else if (type === 'show_warning') {
        violations.push({
          ruleId: rule.id,
          ruleName: rule.name,
          message: rule.errorMessage,
          actionType: 'show_warning',
        });
      }
    }
  }

  return violations;
}

function evaluateCondition(condition: RuleCondition, data: Record<string, unknown>): boolean {
  const value = data[condition.field];

  switch (condition.operator) {
    case 'equals':
      return String(value) === String(condition.value);
    case 'not_equals':
      return String(value) !== String(condition.value);
    case 'is_blank':
      return isBlank(value);
    case 'is_not_blank':
      return !isBlank(value);
    case 'gt':
      return Number(value) > Number(condition.value);
    case 'lt':
      return Number(value) < Number(condition.value);
    default:
      return false;
  }
}

function isBlank(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}
